import { API_BASE_URL } from '../config/api';
import { ensureMp4Faststart } from './mp4Faststart';

interface GenerateUploadUrlParams {
  fileName: string;
  videoId: string;
}

interface GenerateUploadUrlResponse {
  uploadUrl: string;
  location: string;
}

/**
 * Request a signed upload URL for a video asset stored under the given video.
 * Mirrors the audio "add to timeline" storage flow.
 */
const generateUploadUrl = async (
  params: GenerateUploadUrlParams,
  token: string
): Promise<GenerateUploadUrlResponse> => {
  const url = `${API_BASE_URL}/storage/generateUploadUrl?fileName=${encodeURIComponent(
    params.fileName
  )}&videoId=${encodeURIComponent(params.videoId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to generate upload URL: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Upload a video blob to the signed storage URL.
 */
const uploadVideoBlob = async (
  blob: Blob,
  uploadUrl: string
): Promise<boolean> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': blob.type || 'video/mp4',
    },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload video: ${response.statusText}`);
  }

  return true;
};

export interface VideoTrimRange {
  startTime: number;
  endTime: number;
}

export interface TrimGeneratedVideoResult {
  location: string;
  durationSeconds: number;
}

/**
 * Ask the backend to persist a generated video, optionally trimming an exact
 * range and/or removing its audio. The backend downloads the source, stores
 * it under the video's assets folder, and returns the relative blob location.
 */
export const trimGeneratedVideo = async (
  videoUrl: string,
  videoId: string,
  token: string,
  range?: VideoTrimRange,
  mute = false
): Promise<TrimGeneratedVideoResult> => {
  if (!videoUrl) {
    throw new Error('No video URL provided to save');
  }
  if (!videoId) {
    throw new Error('No video id provided to save against');
  }

  const response = await fetch(`${API_BASE_URL}/video/trimVideo`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      videoId,
      sourceUrl: videoUrl,
      ...(range
        ? { startTime: range.startTime, endTime: range.endTime }
        : {}),
      mute,
    }),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(payload?.error || 'Failed to save the generated video');
  }

  return (await response.json()) as TrimGeneratedVideoResult;
};

/**
 * Download the generated (hosted) video and persist it to backend storage,
 * returning the relative blob path to store on the video document.
 *
 * This mirrors the audio "add to timeline" flow (upload SAS + update video).
 * No download URL is generated here: the frontend resolves the relative path
 * into a playable URL using the `sasToken` returned on the video document.
 */
export const handleUploadGeneratedVideo = async (
  videoUrl: string,
  videoId: string,
  token: string
): Promise<string> => {
  if (!videoUrl) {
    throw new Error('No video URL provided to save');
  }

  if (!videoId) {
    throw new Error('No video id provided to save the video against');
  }

  // The generated video is a hosted URL rather than a local file, so download
  // it first so it can be persisted into user storage.
  const videoResponse = await fetch(videoUrl);
  if (!videoResponse.ok) {
    throw new Error(`Failed to download generated video: ${videoResponse.statusText}`);
  }

  const blob = await videoResponse.blob();

  const fileName = `generated-video-${Date.now()}.mp4`;

  // Normalize moov-first before the PUT so playback reaches `loadedmetadata`
  // from the file head instead of tail-chasing across the whole object on
  // slow links. Never fails the upload: on any error the original bytes go up.
  let payload: Blob = blob;
  try {
    const { buffer, applied, reason, atomMap } = ensureMp4Faststart(
      await blob.arrayBuffer()
    );
    console.log('[UploadVideo] faststart:', {
      applied,
      reason,
      atomMap,
      bytes: buffer.byteLength,
    });
    if (applied) payload = new Blob([buffer], { type: 'video/mp4' });
  } catch (error) {
    console.warn(
      '[UploadVideo] faststart skipped, uploading original bytes:',
      (error as Error)?.message
    );
  }

  const uploadUrlData = await generateUploadUrl({ fileName, videoId }, token);

  const uploadSuccess = await uploadVideoBlob(payload, uploadUrlData.uploadUrl);
  if (!uploadSuccess) {
    throw new Error('Video upload failed');
  }

  return uploadUrlData.location;
};